import React, { useMemo } from 'react';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { CHANNEL_COLORS, LEAD_TYPE_LABELS, leadTypesForChannel } from '../../lib/leadMeta';

const { FiSearch, FiX, FiChevronDown, FiChevronUp } = FiIcons;

const STATUS_OPTIONS = [
  { label: 'All Statuses', value: '' },
  { label: 'New', value: 'new' },
  { label: 'Contacted', value: 'contacted' },
  { label: 'Follow Up', value: 'follow_up' },
  { label: 'Appt Set', value: 'appointment_set' },
  { label: 'Closed Won', value: 'closed_won' },
  { label: 'Closed Lost', value: 'closed_lost' },
  { label: 'Do Not Contact', value: 'dnc' },
];

const ROOM_TYPE_OPTIONS = [
  { label: 'All', value: '', color: '#9CA3AF' },
  { label: 'RB', value: 'RB', color: '#22C55E' },
  { label: 'EX', value: 'EX', color: '#F59E0B' },
  { label: 'SK', value: 'SK', color: '#EF4444' },
];

const EXCLUSIVITY_OPTIONS = [
  { label: 'All', value: '' },
  { label: 'Exclusive', value: 'exclusive' },
  { label: 'Non-Exclusive', value: 'non_exclusive' },
  { label: 'Pending Request', value: 'pending_request' },
];

const DATE_CREATED_OPTIONS = [
  { label: 'All', value: '' },
  { label: 'Today', value: 'today' },
  { label: 'This Week', value: 'week' },
  { label: 'This Month', value: 'month' },
  { label: 'Custom Range', value: 'custom' },
];

const LAST_CALLED_OPTIONS = [
  { label: 'All', value: '' },
  { label: 'Never Called', value: 'never' },
  { label: 'Called Today', value: 'today' },
  { label: 'Called This Week', value: 'week' },
  { label: 'Not Called in 30+ Days', value: 'over30' },
];

const SEQUENCE_OPTIONS = [
  { label: 'All', value: '' },
  { label: 'Enrolled', value: 'enrolled' },
  { label: 'Not Enrolled', value: 'not_enrolled' },
];

function ChipGroup({ label, options, value, onChange, colored = false }) {
  return (
    <div className="flex flex-col gap-1 min-w-0">
      {label && <span className="text-[10px] font-semibold uppercase tracking-wide text-gray-500">{label}</span>}
      <div className="flex flex-wrap gap-1">
        {options.map((opt) => {
          const selected = value === opt.value;
          const bg =
            selected && colored && opt.color
              ? opt.color
              : selected && colored && opt.value && CHANNEL_COLORS[opt.value]
                ? CHANNEL_COLORS[opt.value]
                : null;
          return (
            <button
              key={opt.value || 'all'}
              type="button"
              onClick={() => onChange(opt.value)}
              className={`h-7 px-2.5 rounded-md text-xs font-medium transition-colors border ${
                selected
                  ? 'text-white border-transparent shadow-sm'
                  : 'bg-white text-gray-600 border-gray-200 hover:border-gray-300'
              }`}
              style={bg ? { backgroundColor: bg } : undefined}
            >
              {opt.label}
            </button>
          );
        })}
      </div>
    </div>
  );
}

function SelectFilter({ label, value, onChange, options }) {
  return (
    <label className="flex flex-col gap-0.5 min-w-[120px]">
      <span className="text-[10px] font-semibold uppercase tracking-wide text-gray-500">{label}</span>
      <select
        className="h-8 px-2 text-xs rounded-md border border-gray-300 bg-white focus:outline-none focus:ring-2 focus:ring-primary"
        value={value}
        onChange={(e) => onChange(e.target.value)}
      >
        {options.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
    </label>
  );
}

export default function LeadsFilters({
  localSearch,
  setLocalSearch,
  filters,
  setFilters,
  agents,
  pipelineStages = [],
  importLabels = [],
  eventTitles = [],
  isRecruits,
  activeFilterCount,
  onClearAll,
  showAdvanced,
  setShowAdvanced,
  currentUserId = '',
}) {
  const set = (key) => (val) =>
    setFilters((prev) => {
      const next = { ...prev, [key]: val };
      if (key === 'channel' && prev.type) {
        const allowed = leadTypesForChannel(val);
        if (!allowed.includes(prev.type)) next.type = '';
      }
      if (key === 'dateCreated' && val !== 'custom') {
        next.dateCreatedFrom = '';
        next.dateCreatedTo = '';
      }
      return next;
    });

  const typeOptions = useMemo(() => {
    const base = [{ label: 'All Types', value: '' }];
    if (isRecruits) return [...base, { label: 'Recruit', value: 'recruit' }];
    const types = filters.channel
      ? leadTypesForChannel(filters.channel).filter((t) => t !== 'recruit')
      : Object.keys(LEAD_TYPE_LABELS).filter((t) => t !== 'recruit');
    return [...base, ...types.map((value) => ({ label: LEAD_TYPE_LABELS[value], value }))];
  }, [filters.channel, isRecruits]);

  const agentOptions = useMemo(() => {
    const others = agents
      .filter((a) => a.id !== currentUserId)
      .map((a) => ({
        label: a.full_name?.trim() || a.email || 'Unknown',
        value: a.id,
      }));
    const mine = currentUserId
      ? [{ label: 'My Leads', value: currentUserId }]
      : [];
    return [{ label: 'All Agents', value: '' }, ...mine, ...others];
  }, [agents, currentUserId]);

  const stageOptions = [
    { label: 'All Stages', value: '' },
    ...pipelineStages.map((s) => ({ label: s, value: s })),
  ];

  const importLabelOptions = [
    { label: 'All Batches', value: '' },
    ...importLabels.map((l) => ({ label: l, value: l })),
  ];

  const eventOptions = [
    { label: 'All Events', value: '' },
    ...eventTitles.map((e) => ({ label: e, value: e })),
  ];

  return (
    <div className="px-4 py-3 border-b border-gray-200 bg-gray-50 space-y-3">
      <div className="flex items-center gap-2 flex-wrap">
        <div className="relative flex-1 min-w-[180px]">
          <SafeIcon icon={FiSearch} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4 pointer-events-none" />
          <input
            type="text"
            placeholder="Search by name, phone, email…"
            className="h-9 w-full pl-9 pr-3 text-xs rounded-md border border-gray-300 bg-white focus:outline-none focus:ring-2 focus:ring-primary"
            value={localSearch}
            onChange={(e) => setLocalSearch(e.target.value)}
          />
        </div>

        {!isRecruits && (
          <ChipGroup
            label="Channel"
            options={[
              { label: 'All', value: '', color: '#9CA3AF' },
              { label: 'LD', value: 'LD', color: CHANNEL_COLORS.LD },
              { label: 'IM', value: 'IM', color: CHANNEL_COLORS.IM },
              { label: 'AM', value: 'AM', color: CHANNEL_COLORS.AM },
            ]}
            value={filters.channel}
            onChange={set('channel')}
            colored
          />
        )}

        {activeFilterCount > 0 && (
          <button
            type="button"
            onClick={onClearAll}
            className="flex items-center gap-1 h-9 px-3 text-xs text-red-500 hover:text-red-700 font-medium rounded-md hover:bg-red-50 border border-red-200"
          >
            <SafeIcon icon={FiX} className="w-3.5 h-3.5" />
            Clear ({activeFilterCount})
          </button>
        )}

        <button
          type="button"
          onClick={() => setShowAdvanced((v) => !v)}
          className="ml-auto flex items-center gap-1 h-9 px-3 text-xs font-medium text-gray-600 border border-gray-200 rounded-md bg-white hover:bg-gray-50"
        >
          {showAdvanced ? 'Less filters' : 'More filters'}
          <SafeIcon icon={showAdvanced ? FiChevronUp : FiChevronDown} className="w-4 h-4" />
        </button>
      </div>

      <div className="flex flex-wrap items-end gap-3">
        {!isRecruits && (
          <SelectFilter label="Lead Type" value={filters.type} onChange={set('type')} options={typeOptions} />
        )}
        <SelectFilter label="Status" value={filters.status} onChange={set('status')} options={STATUS_OPTIONS} />
        <SelectFilter label="Agent" value={filters.agent} onChange={set('agent')} options={agentOptions} />
      </div>

      {showAdvanced && (
        <div className="pt-2 border-t border-gray-200 space-y-3">
          <ChipGroup label="Room Type" options={ROOM_TYPE_OPTIONS} value={filters.roomType} onChange={set('roomType')} colored />
          <ChipGroup
            label="Exclusivity"
            options={EXCLUSIVITY_OPTIONS}
            value={filters.exclusivityStatus}
            onChange={set('exclusivityStatus')}
          />
          <ChipGroup
            label="Date Created"
            options={DATE_CREATED_OPTIONS}
            value={filters.dateCreated}
            onChange={set('dateCreated')}
          />
          {filters.dateCreated === 'custom' && (
            <div className="flex flex-wrap gap-2 items-end">
              <label className="text-xs text-gray-600">
                From
                <input
                  type="date"
                  className="block mt-0.5 h-8 px-2 text-xs border border-gray-300 rounded-md"
                  value={filters.dateCreatedFrom}
                  onChange={(e) => set('dateCreatedFrom')(e.target.value)}
                />
              </label>
              <label className="text-xs text-gray-600">
                To
                <input
                  type="date"
                  className="block mt-0.5 h-8 px-2 text-xs border border-gray-300 rounded-md"
                  value={filters.dateCreatedTo}
                  onChange={(e) => set('dateCreatedTo')(e.target.value)}
                />
              </label>
            </div>
          )}
          <ChipGroup label="Last Called" options={LAST_CALLED_OPTIONS} value={filters.lastCalled} onChange={set('lastCalled')} />
          <ChipGroup label="Sequence" options={SEQUENCE_OPTIONS} value={filters.sequenceEnrolled} onChange={set('sequenceEnrolled')} />
          <div className="flex flex-wrap gap-4 items-center">
            <label className="flex items-center gap-2 text-xs text-gray-700 cursor-pointer">
              <input
                type="checkbox"
                className="rounded border-gray-300 text-primary focus:ring-primary"
                checked={filters.hasEmail}
                onChange={(e) => set('hasEmail')(e.target.checked)}
              />
              Has email
            </label>
            <label className="flex items-center gap-2 text-xs text-gray-700 cursor-pointer">
              <input
                type="checkbox"
                className="rounded border-gray-300 text-primary focus:ring-primary"
                checked={filters.hasPhone}
                onChange={(e) => set('hasPhone')(e.target.checked)}
              />
              Has phone
            </label>
            <label className="flex items-center gap-2 text-xs text-gray-700 cursor-pointer">
              <input
                type="checkbox"
                className="rounded border-gray-300 text-primary focus:ring-primary"
                checked={filters.splitApplies}
                onChange={(e) => set('splitApplies')(e.target.checked)}
              />
              Split applies only
            </label>
          </div>
          <div className="flex flex-wrap items-end gap-3">
            <SelectFilter
              label="Pipeline Stage"
              value={filters.pipelineStage}
              onChange={set('pipelineStage')}
              options={stageOptions}
            />
            <SelectFilter
              label="Import Batch"
              value={filters.importLabel}
              onChange={set('importLabel')}
              options={importLabelOptions}
            />
            <SelectFilter
              label="Event"
              value={filters.eventTitle}
              onChange={set('eventTitle')}
              options={eventOptions}
            />
          </div>
        </div>
      )}
    </div>
  );
}
